import clsx from "clsx";
import React from "react";
import { createPluggable } from "pluggable-react";
import Avatar, { type AvatarProps } from "../Avatar/Avatar";
import styles from "./AvatarGroup.module.scss";

const SPACINGS = {
  small: -16,
  medium: null,
} as const;

export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  max?: number;
  spacing?: "small" | "medium" | number;
  size?: AvatarProps["size"];
}

const DefaultAvatarGroup = ({
  children: childrenProp,
  className,
  max = 5,
  spacing = "medium",
  size = "lg",
  ...other
}: AvatarGroupProps) => {
  const clampedMax = max < 2 ? 2 : max;

  const children = React.Children.toArray(childrenProp).filter((child) => {
    if (React.isValidElement(child) && child.type === React.Fragment) {
      console.error("AvatarGroup: Fragment children are not supported. Consider providing an array instead.");
    }
    return React.isValidElement(child);
  });

  const extraAvatars = children.length > clampedMax ? children.length - clampedMax + 1 : 0;
  const marginLeft =
    typeof spacing === "string" && SPACINGS[spacing] !== undefined ? SPACINGS[spacing] : -(spacing as number);

  return (
    <div className={clsx(styles.group, className)} data-deci-avatar-group="true" {...other}>
      {children.slice(0, children.length - extraAvatars).map((child: any, index) =>
        React.cloneElement(child, {
          className: clsx(child.props.className, styles.groupItem),
          style: {
            zIndex: index,
            marginLeft: index === 0 ? undefined : (marginLeft ?? -8),
            ...child.props.style,
          },
        }),
      )}
      {extraAvatars ? (
        <Avatar
          invert
          size={size}
          className={styles.groupItem}
          style={{
            zIndex: children.length + 1,
            marginLeft: marginLeft ?? -8,
          }}
        >
          +{extraAvatars}
        </Avatar>
      ) : null}
    </div>
  );
};

const AvatarGroup = createPluggable<AvatarGroupProps>("deci.avatarGroup", DefaultAvatarGroup);

export default AvatarGroup;
